import { z } from 'zod';

/**
 * Потолки из docs/plans/2026-09-18-custom-criteria.md, §8. Одинаковы для всех
 * тарифов: лимит по тарифу ограничивает число критериев ниже `perOrg`, а не
 * длину их текста.
 *
 * `perOrg` — наибольшее число СОБСТВЕННЫХ критериев, которое может дать тариф
 * (`max_call_criteria` в `@aica/stripe` его не превышает). Потолок организации
 * — уже не он, а `criteriaCeilingFor(лимит тарифа)`: собственные плюс каталог
 * встроенных (#868, спека встроенных §5). Набор, который видит один звонок,
 * поэтому доходит до `criteriaCeilingFor(perOrg)`.
 */
export const CALL_CRITERION_LIMITS = {
  name: 60,
  question: 300,
  // 800, а не 500 (спека Р10): точность критерия определяет текст подсказки,
  // главный рычаг в нём — явные исключения, а подсказка с исключениями на
  // замере 18.09 вышла 535 символов.
  guidance: 800,
  options: 10,
  /** Наибольший лимит собственных по тарифам; не потолок организации (см. выше). */
  perOrg: 30,
} as const;

/** Встроенное значение каждого списка: цитаты не требует. */
export const NOT_MENTIONED_CODE = 'not_mentioned';

/**
 * Алфавит кода значения списка — и ключа критерия. Один на всех: его читают
 * схема определения здесь и разбор условия фильтра списка звонков (#865,
 * `@aica/api-types`), где на отсутствии `:` и `,` держится формат строки.
 */
export const CALL_CRITERION_CODE_PATTERN = /^[a-z][a-z0-9_]{0,39}$/;

/**
 * Сколько архивных критериев показывают раздел критериев и фильтр списка
 * звонков (#865). Архив не упирается ни в тариф, ни в потолок и растёт без
 * предела — берутся последние по времени архива. Одно число на оба места:
 * иначе фильтр предлагал бы не тот архив, что виден в разделе.
 */
export const CALL_CRITERIA_ARCHIVED_SHOWN = 100;

/**
 * Текстовые поля определения — общие с запросами API (`@aica/api-types`,
 * `call-criteria.ts`), чтобы потолки длин жили в одном месте.
 */
export const callCriterionTextFields = {
  name: z.string().trim().min(1).max(CALL_CRITERION_LIMITS.name),
  question: z.string().trim().min(1).max(CALL_CRITERION_LIMITS.question),
  guidance: z.string().trim().max(CALL_CRITERION_LIMITS.guidance).optional(),
};

const base = {
  /**
   * Стабильный ключ. Становится именем свойства strict-схемы ответа
   * (`c_<key>`), поэтому алфавит узкий.
   */
  key: z.string().regex(CALL_CRITERION_CODE_PATTERN),
  ...callCriterionTextFields,
};

export const CallCriterionOptionSchema = z.object({
  code: z
    .string()
    .regex(CALL_CRITERION_CODE_PATTERN)
    .refine((code) => code !== NOT_MENTIONED_CODE, 'код зарезервирован'),
  label: z.string().trim().min(1).max(CALL_CRITERION_LIMITS.name),
  needsEvidence: z.boolean(),
});

/**
 * Список слов встроенного `forbidden_words` (#2784): клиент задаёт его в
 * панели критерия, формулировка остаётся нашей. Потолки — прежние потолки
 * списка проекта: список уходит в каждый вызов шага критериев и оплачивается
 * токенами, 100 × 64 символа ≈ 1,5–2 K токенов.
 */
export const CALL_CRITERION_WORD_LIST_LIMITS = { words: 100, length: 64 } as const;

/**
 * Строки без пробелов по краям, без пустых и без повторов без учёта регистра:
 * «Дорого» и «дорого» для модели — одно слово, а в потолок они вошли бы
 * дважды. Нормализует, а не отвергает: список приходит из поля ввода.
 */
export const CallCriterionWordListSchema = z
  .array(z.string().trim().max(CALL_CRITERION_WORD_LIST_LIMITS.length))
  .transform((words) => {
    const seen = new Set<string>();
    return words.filter((word) => {
      const folded = word.toLocaleLowerCase();
      if (!word || seen.has(folded)) return false;
      seen.add(folded);
      return true;
    });
  })
  .pipe(z.array(z.string()).max(CALL_CRITERION_WORD_LIST_LIMITS.words));

export const CallCriterionSchema = z.discriminatedUnion('resultType', [
  z.object({
    ...base,
    resultType: z.literal('flag'),
    looksFor: z.enum(['presence', 'absence']),
    /** Только у встроенного `forbidden_words`; непустой — пустой не оценивается. */
    wordList: z
      .array(z.string().min(1).max(CALL_CRITERION_WORD_LIST_LIMITS.length))
      .min(1)
      .max(CALL_CRITERION_WORD_LIST_LIMITS.words)
      .optional(),
  }),
  z.object({ ...base, resultType: z.literal('score') }),
  z.object({
    ...base,
    resultType: z.literal('choice'),
    options: z
      .array(CallCriterionOptionSchema)
      .min(1)
      .max(CALL_CRITERION_LIMITS.options)
      .refine(
        (options) => new Set(options.map((o) => o.code)).size === options.length,
        'коды значений не повторяются'
      ),
  }),
]);

export type CallCriterion = z.infer<typeof CallCriterionSchema>;
